import {
	candidateEmailIndexQueryKey,
	pipelineStageShowQueryKey,
	useCandidateEmailIndex,
	useCandidateEmailMarkAllAsRead,
	useCandidateEmailMarkAsRead,
	useCandidateEmailMarkAsUnread,
	useCandidateEmailStore,
} from '@jobilla/nexus-sdk'
import type {
	CandidateEmailData,
	CandidateEmailIndex403,
	CandidateEmailIndexQueryResponse,
	CandidateEmailMarkAsReadMutationResponse,
	CandidateEmailMarkAsUnreadMutationResponse,
	CandidateEmailStore403,
	CandidateEmailStore422,
	CandidateEmailStoreMutationRequest,
	CandidateEmailStoreMutationResponse,
} from '@jobilla/nexus-sdk'
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

type OptimisticContext = { previousResponse: CandidateEmailIndexQueryResponse | undefined }

export type UseCandidateEmailsReturn = {
	emails: CandidateEmailData[]
	isLoading: boolean
	error: CandidateEmailIndex403 | null
	refetch: (
		options?: RefetchOptions,
	) => Promise<QueryObserverResult<CandidateEmailIndexQueryResponse, CandidateEmailIndex403>>
	sendEmail: (data: CandidateEmailStoreMutationRequest) => Promise<CandidateEmailStoreMutationResponse>
	isSending: boolean
	sendError: CandidateEmailStore403 | CandidateEmailStore422 | null
	markAsRead: (emailIds?: string[]) => Promise<CandidateEmailMarkAsReadMutationResponse>
	markAsUnread: (emailIds: string[]) => Promise<CandidateEmailMarkAsUnreadMutationResponse>
	markAllAsRead: () => Promise<unknown>
	isMarkingRead: boolean
}

export function useCandidateEmails(candidateId: string, campaignId?: string): UseCandidateEmailsReturn {
	const queryClient = useQueryClient()

	const emailQueryKey = candidateEmailIndexQueryKey(candidateId)

	const invalidateEmailQueries = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: emailQueryKey })
		if (campaignId) {
			void queryClient.invalidateQueries({ queryKey: pipelineStageShowQueryKey(campaignId) })
		}
	}, [queryClient, emailQueryKey, campaignId])

	const createOptimisticContext = useCallback(async (): Promise<OptimisticContext> => {
		await queryClient.cancelQueries({ queryKey: emailQueryKey })
		return { previousResponse: queryClient.getQueryData<CandidateEmailIndexQueryResponse>(emailQueryKey) }
	}, [queryClient, emailQueryKey])

	const rollbackOnError = useCallback(
		(_err: unknown, _variables: unknown, context: unknown) => {
			const ctx = context as OptimisticContext | undefined
			if (ctx?.previousResponse) {
				queryClient.setQueryData(emailQueryKey, ctx.previousResponse)
			}
		},
		[queryClient, emailQueryKey],
	)

	const updateEmailsOptimistically = useCallback(
		(predicate: (email: CandidateEmailData) => boolean, update: Partial<CandidateEmailData>) => {
			queryClient.setQueryData<CandidateEmailIndexQueryResponse>(emailQueryKey, (old) => {
				if (!old) {
					return old
				}
				return old.map((email) => (predicate(email) ? { ...email, ...update } : email))
			})
		},
		[queryClient, emailQueryKey],
	)

	const emailsQuery = useCandidateEmailIndex(candidateId, {
		enabled: Boolean(candidateId),
	})

	const sendEmailMutation = useCandidateEmailStore({
		mutationKey: ['candidate-email-send', candidateId],
		onSuccess: invalidateEmailQueries,
	})

	const markAsReadMutation = useCandidateEmailMarkAsRead({
		mutationKey: ['candidate-email-mark-read', candidateId],
		onMutate: async (variables) => {
			const context = await createOptimisticContext()
			updateEmailsOptimistically(
				(
					email,
				) => (variables?.data?.emailIds
					? variables.data.emailIds.includes(email.id)
					: email.direction === 'inbound'),
				{ isRead: true },
			)
			return context
		},
		onError: rollbackOnError,
		onSettled: invalidateEmailQueries,
	})

	const markAllAsReadMutation = useCandidateEmailMarkAllAsRead({
		mutationKey: ['candidate-email-mark-all-read', candidateId],
		onMutate: async () => {
			const context = await createOptimisticContext()
			updateEmailsOptimistically((email) => email.direction === 'inbound', { isRead: true })
			return context
		},
		onError: rollbackOnError,
		onSettled: invalidateEmailQueries,
	})

	const markAsUnreadMutation = useCandidateEmailMarkAsUnread({
		mutationKey: ['candidate-email-mark-unread', candidateId],
		onMutate: async (variables) => {
			const context = await createOptimisticContext()
			updateEmailsOptimistically((email) => variables.data.emailIds.includes(email.id), { isRead: false })
			return context
		},
		onError: rollbackOnError,
		onSettled: invalidateEmailQueries,
	})

	return {
		emails: emailsQuery.data ?? [],
		isLoading: emailsQuery.isLoading,
		error: emailsQuery.error,
		refetch: emailsQuery.refetch,
		sendEmail: (data: CandidateEmailStoreMutationRequest) => sendEmailMutation.mutateAsync({ candidateId, data }),
		isSending: sendEmailMutation.isPending,
		sendError: sendEmailMutation.error,
		markAsRead: (emailIds?: string[]) =>
			markAsReadMutation.mutateAsync({ candidateId, data: emailIds ? { emailIds } : {} }),
		markAsUnread: (emailIds: string[]) => markAsUnreadMutation.mutateAsync({ candidateId, data: { emailIds } }),
		markAllAsRead: () => markAllAsReadMutation.mutateAsync({ candidateId }),
		isMarkingRead: markAsReadMutation.isPending || markAllAsReadMutation.isPending,
	}
}
